import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { FAQRequest, FAQResponse, KnowledgeBaseService } from "services/api-workers-service"
import { SentryService } from "services/SentryService"
import { RootState } from "store/store"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button, { IconButton } from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import Spinner from "ui/components/atoms/spinner"
import { Label, P, Title } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import { formatDate } from "utils/date"
import AddFAQModal from "views/Profile/components/modals/AddFAQModal"

const FAQItem = ({
  faq,
  onEdit,
  onDelete,
}: {
  faq: FAQResponse
  onEdit: () => void
  onDelete: () => void
}) => {
  const { t, i18n } = useTranslation(["panel"])

  return (
    <FAQWrapper>
      <Box $flex={1}>
        <Label $marginBottom="XS">{faq.question}</Label>
        <P $small $marginBottom="XS">
          {faq.answer.length > 150 ? `${faq.answer.substring(0, 150)}...` : faq.answer}
        </P>
        <P $small $marginBottom="0">
          {formatDate(faq.updated_at || faq.created_at, i18n.language)}
        </P>
      </Box>
      <ActionsRow>
        <IconButton
          $size="45px"
          $borderRadius="borderRadius"
          $iconSize="M"
          $background="background"
          icon="edit"
          onClick={onEdit}
          ariaLabel="Edit"
        />
        <IconButton
          $size="45px"
          $borderRadius="borderRadius"
          $iconSize="M"
          $color="red"
          $background="redBackground"
          icon="trash"
          onClick={onDelete}
          ariaLabel="Delete"
        />
      </ActionsRow>
    </FAQWrapper>
  )
}

const ProfileKnowledgeBaseFAQsView = () => {
  const { t } = useTranslation(["panel", "common"])
  const { profile } = useSelector((state: RootState) => state.profile)
  const [faqs, setFAQs] = useState<FAQResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [editingFAQ, setEditingFAQ] = useState<FAQResponse | null>(null)

  const fetchFAQs = useCallback(async () => {
    if (!profile?.id) return

    try {
      setLoading(true)
      setError(null)
      const data = await KnowledgeBaseService.getFAQs()
      setFAQs(data)
    } catch (e) {
      SentryService.error("[ERROR KnowledgeBaseService.getFAQs]:", e)
      setError(t("profile.knowledge-base.faqs.error.load"))
    } finally {
      setLoading(false)
    }
  }, [profile?.id, t])

  useEffect(() => {
    fetchFAQs()
  }, [fetchFAQs])

  const handleSave = useCallback(
    async (allFAQs: FAQRequest[]) => {
      if (!profile?.id) return

      try {
        setSaving(true)
        setError(null)
        const updated = await KnowledgeBaseService.updateFAQs(allFAQs)
        setFAQs(updated)
      } catch (e) {
        SentryService.error("[ERROR KnowledgeBaseService.updateFAQs]:", e)
        setError(t("profile.knowledge-base.faqs.error.save"))
      } finally {
        setSaving(false)
      }
    },
    [profile?.id, t],
  )

  const handleModalSubmit = useCallback(
    (faqData: FAQRequest) => {
      if (editingFAQ) {
        // Update existing FAQ
        const updatedFAQs: FAQRequest[] = faqs.map(faq =>
          faq.id === editingFAQ.id ? faqData : { question: faq.question, answer: faq.answer },
        )
        handleSave(updatedFAQs)
      } else {
        // Add new FAQ
        const updatedFAQs: FAQRequest[] = [
          ...faqs.map(f => ({ question: f.question, answer: f.answer })),
          faqData,
        ]
        handleSave(updatedFAQs)
      }
      setShowModal(false)
      setEditingFAQ(null)
    },
    [faqs, editingFAQ, handleSave],
  )

  const handleDelete = useCallback(
    (id: string) => {
      if (window.confirm(t("profile.knowledge-base.faqs.confirm-delete"))) {
        const updatedFAQs: FAQRequest[] = faqs
          .filter(faq => faq.id !== id)
          .map(faq => ({
            question: faq.question,
            answer: faq.answer,
          }))
        handleSave(updatedFAQs)
      }
    },
    [faqs, handleSave, t],
  )

  const handleAdd = () => {
    setEditingFAQ(null)
    setShowModal(true)
  }

  const handleEdit = (faq: FAQResponse) => {
    setEditingFAQ(faq)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingFAQ(null)
  }

  const filteredFAQs = faqs.filter(
    faq =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  return (
    <>
      <Wrapper>
        <Box $marginBottom="S">
          <Title $marginBottom="XS">{t("profile.knowledge-base.faqs.title")}</Title>
          <P $small $marginBottom="0">
            {t("profile.knowledge-base.faqs.text")}
          </P>
        </Box>

        {error && (
          <Alert $type="error" $marginBottom="S">
            {error}
          </Alert>
        )}

        <Row $justifyContent="space-between" $alignItems="center" $marginBottom="S">
          <Box $flex={1} $marginRight="S">
            <Input
              name="search"
              placeholder={t("profile.knowledge-base.faqs.search-placeholder")}
              value={searchQuery}
              onChange={({ name, value }) => setSearchQuery(value as string)}
            />
          </Box>
          <Button icon="plus" onClick={handleAdd} disabled={saving}>
            {t("profile.knowledge-base.faqs.add")}
          </Button>
        </Row>

        <Content>
          {loading && (
            <Row $justifyContent="center">
              <Spinner $size="30px" />
            </Row>
          )}

          {!loading && filteredFAQs.length === 0 && !searchQuery && (
            <P $small $marginBottom="0">
              {t("profile.knowledge-base.faqs.empty")}
            </P>
          )}

          {!loading && filteredFAQs.length === 0 && searchQuery && (
            <P $small $marginBottom="0">
              {t("profile.knowledge-base.faqs.no-results")}
            </P>
          )}

          {!loading &&
            filteredFAQs.map(faq => (
              <FAQItem
                key={faq.id}
                faq={faq}
                onEdit={() => handleEdit(faq)}
                onDelete={() => handleDelete(faq.id)}
              />
            ))}
        </Content>
      </Wrapper>
      <AddFAQModal
        show={showModal}
        setShow={handleCloseModal}
        onSubmit={handleModalSubmit}
        initialData={
          editingFAQ ? { question: editingFAQ.question, answer: editingFAQ.answer } : null
        }
        loading={saving}
      />
    </>
  )
}

export default ProfileKnowledgeBaseFAQsView

const FAQWrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  display: flex;
  align-items: flex-start;
  grid-gap: ${({ theme }) => theme.space.S};
`

const ActionsRow = styled(Row)`
  grid-gap: ${({ theme }) => theme.space.XS};
`

const Content = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.S};
`

const Wrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.M} 0;
`
